<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="false"
    :size="shouldHideRoles ? '' : 'lg'"
    centered
    :class="modalClass"
    @update:show="handleClose"
  >
    <template #header>
      <div>
        <div class="d-flex align-items-center">
          <CIcon :name="headerIcon" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">
              <slot name="title"/>
            </h5>
          </div>
        </div>
      </div>
      <CButtonClose @click="close"/>
    </template>

    <div>
      <div v-if="canBypassLimits" class="mb-3">
        <CBadge color="warning" class="badge-outline">
          <CIcon name="cipShield01" size="sm" class="mr-1"/>
          <translate translate-context="collaborators.form">Admin - Limits bypassed</translate>
        </CBadge>
      </div>

      <!-- Description -->
      <PAlert color="primary" class="mb-3">
        <slot name="description"/>
      </PAlert>

      <!-- Entry field (email or username) -->
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="font-weight-medium mb-0">
            <slot name="entry-label"/>
          </label>
          <CBadge v-if="entries.length > 0" color="info" class="badge-outline">
            {{ entries.length }} <slot name="entry-count-label"/>
          </CBadge>
        </div>

        <CInput
          ref="entryInput"
          v-model="currentInput"
          type="text"
          :placeholder="entryPlaceholder"
          :disabled="isEntryLimitReached"
          :maxlength="entryType === 'username' ? 50 : undefined"
          :pattern="entryType === 'username' ? '[a-zA-Z0-9_.]+' : undefined"
          @keydown.enter.prevent="handleAddEntry"
          class="mb-2"
        />

        <div v-if="isEntryLimitReached" class="text-warning small">
          <CIcon name="cipAlertTriangle" size="sm" class="mr-1"/>
          <translate translate-context="collaborators.limits"
                     :translate-params="{max: maxNewEntries}">
            Limit reached: maximum %{max} collaborator(s) for the selected role(s)
          </translate>
        </div>
        <div v-else-if="entryError" class="text-danger">
          {{ entryError }}
        </div>

        <div v-if="entries.length > 0" class="mt-3">
          <div class="d-flex flex-wrap gap-2">
            <CBadge
              v-for="(entry, index) in entries"
              :key="index"
              color="info"
              class="badge-outline d-flex align-items-center"
            >
              {{ entry }}
              <div class="cursor-pointer" @click="removeEntry(index)">
                <CIcon name="cipXCircle" size="sm" class="ml-1"/>
              </div>
            </CBadge>
          </div>
        </div>
      </div>

      <!-- Roles Selection -->
      <div v-if="!shouldHideRoles" class="mb-3">
        <label class="font-weight-medium">
          <translate translate-context="collaborators.form">Roles</translate>
        </label>
        <RoleGrid
          v-model="selectedRoles"
          :roles="filteredAvailableRoles"
          :role-limits="roleLimitsForGrid"
          :show-limits="showRoleLimits"
          @show-permissions="handleShowPermissions"
        />
        <div v-if="hasLimitsConfigured && !canBypassLimits && filteredAvailableRoles.length === 0"
             class="text-warning mt-2">
          <CIcon name="cipAlertTriangle" size="custom-size" :height="18" class="mr-1"/>
          <slot name="no-roles-text"/>
        </div>
      </div>

      <!-- Personal Message (optional) -->
      <div v-if="showPersonalMessage" class="mb-3">
        <label class="font-weight-medium">
          <translate translate-context="collaborators.form">Personal Message (Optional)</translate>
        </label>
        <CTextarea
          v-model="personalMessage"
          :placeholder="$gettext('Add a personal note for the invitation email...')"
          :maxlength="1000"
          rows="3"
          class="mb-1"
        />
        <div class="text-muted">
          {{ personalMessage.length }}/1000
        </div>
      </div>
    </div>

    <template #footer>
      <CButton color="secondary" @click="close">
        <translate translate-context="collaborators.form">Cancel</translate>
      </CButton>
      <CButton color="primary" @click="handleSubmit" :disabled="!canSubmit">
        <slot name="submit-text"/>
      </CButton>
    </template>

    <RolePermissionsModal
      ref="rolePermissionsModal"
      :scope="scope"
    />
  </CModal>
</template>

<script>
import {mapActions} from 'vuex'
import RoleGrid from './RoleGrid.vue'
import RolePermissionsModal from './RolePermissionsModal.vue'
import PAlert from '@/components/PAlert.vue'

export default {
  name: 'CollaboratorFormModal',

  components: {
    PAlert,
    RoleGrid,
    RolePermissionsModal
  },

  props: {
    scope: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    },
    limitsUsage: {
      type: Object,
      default: null
    },
    canBypassLimits: {
      type: Boolean,
      default: false
    },
    entryType: {
      type: String,
      default: 'email',
      validator: v => ['email', 'username'].includes(v)
    },
    headerIcon: {
      type: String,
      default: 'cipUsersPlus'
    },
    showPersonalMessage: {
      type: Boolean,
      default: false
    },
    modalClass: {
      type: String,
      default: 'modal-i-user'
    },
    entryPlaceholder: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isVisible: false,
      entries: [],
      currentInput: '',
      selectedRoles: {},
      personalMessage: '',
      entryError: ''
    }
  },

  computed: {
    canSubmit() {
      const hasEntries = this.entries.length > 0 || this.currentInput.trim().length > 0
      const hasRoles = this.shouldHideRoles || Object.values(this.selectedRoles).some(v => v)
      const withinLimits = this.maxNewEntries === Infinity || this.entries.length <= this.maxNewEntries
      return hasEntries && hasRoles && withinLimits
    },

    hasLimitsConfigured() {
      return this.limitsUsage?.configured === true
    },

    showRoleLimits() {
      return this.hasLimitsConfigured && !this.canBypassLimits
    },

    roleLimitsForGrid() {
      if (!this.limitsUsage?.role_limits) {
        return null
      }
      return this.limitsUsage.role_limits
    },

    filteredAvailableRoles() {
      if (this.canBypassLimits || !this.hasLimitsConfigured) {
        return this.availableRoles
      }
      const allowedRoleCodes = Object.keys(this.limitsUsage.role_limits || {})
      return this.availableRoles.filter(role => {
        return role.code && allowedRoleCodes.includes(role.code)
      })
    },

    isAdvancedUser() {
      return this.hasPerm('collaborator.advanced')
    },

    shouldHideRoles() {
      return this.filteredAvailableRoles.length === 1 && !this.isAdvancedUser
    },

    maxNewEntries() {
      if (this.canBypassLimits || !this.hasLimitsConfigured) {
        return Infinity
      }

      const limits = []

      if (this.limitsUsage?.total_limit?.available != null) {
        limits.push(this.limitsUsage.total_limit.available)
      }

      const selectedRoleIds = Object.keys(this.selectedRoles).filter(id => this.selectedRoles[id])
      if (selectedRoleIds.length > 0 && this.limitsUsage?.role_limits) {
        for (const roleId of selectedRoleIds) {
          const role = this.availableRoles.find(r => r.id === parseInt(roleId))
          if (role?.code && this.limitsUsage.role_limits[role.code]) {
            limits.push(this.limitsUsage.role_limits[role.code].available)
          }
        }
      }

      if (limits.length === 0) return Infinity
      return Math.max(0, Math.min(...limits))
    },

    isEntryLimitReached() {
      return this.maxNewEntries !== Infinity && this.entries.length >= this.maxNewEntries
    }
  },

  methods: {
    ...mapActions('collaborators', [
      'fetchRolePermissions'
    ]),

    open() {
      this.isVisible = true
      this.resetForm()
      this.$nextTick(() => {
        this.$refs.entryInput?.$el?.focus()
      })
    },

    close() {
      this.isVisible = false
    },

    handleClose(val) {
      if (!val) {
        this.close()
      }
    },

    resetForm() {
      this.entries = []
      this.currentInput = ''
      this.selectedRoles = {}
      this.personalMessage = ''
      this.entryError = ''
      this.$nextTick(() => this.preselectSingleRole())
    },

    preselectSingleRole() {
      const roles = this.filteredAvailableRoles
      if (roles.length === 1) {
        this.selectedRoles = {[roles[0].id]: true}
      }
    },

    handleAddEntry() {
      if (this.entryType === 'email') {
        this.addEmail()
      } else {
        this.addUsername()
      }
    },

    addEmail() {
      const email = this.currentInput.trim().toLowerCase()
      this.entryError = ''

      if (!email) return

      if (this.isEntryLimitReached) {
        this.entryError = this.$pgettext('collaborators.limits', 'Maximum number of collaborators reached for the selected role(s)')
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.entryError = this.$pgettext('collaborators.notification', 'Invalid email format')
        return
      }

      if (this.entries.includes(email)) {
        this.entryError = this.$pgettext('collaborators.notification', 'Email already added')
        return
      }

      this.entries.push(email)
      this.currentInput = ''
    },

    addUsername() {
      const username = this.currentInput.trim()
      this.entryError = ''

      if (!username) return

      if (this.isEntryLimitReached) {
        this.entryError = this.$pgettext('collaborators.limits', 'Maximum number of collaborators reached for the selected role(s)')
        return
      }

      const usernameRegex = /^[a-zA-Z0-9_.]+$/
      if (!usernameRegex.test(username)) {
        this.entryError = this.$pgettext('collaborators.virtualusers', 'Only letters, numbers, underscore and dot are allowed')
        return
      }

      if (this.entries.includes(username)) {
        this.entryError = this.$pgettext('collaborators.virtualusers', 'Username already added')
        return
      }

      this.entries.push(username)
      this.currentInput = ''
    },

    removeEntry(index) {
      this.entries.splice(index, 1)
    },

    async handleShowPermissions(role) {
      const permissions = await this.fetchRolePermissions(role.id)
      this.$refs.rolePermissionsModal.show(permissions, role)
    },

    handleSubmit() {
      if (!this.canSubmit) return

      // Auto-add pending input
      if (this.currentInput.trim()) {
        this.handleAddEntry()
        if (this.entryError) return
      }

      // Auto-select the only role when hidden
      if (this.shouldHideRoles) {
        this.preselectSingleRole()
      }

      const roleIds = Object.keys(this.selectedRoles)
        .filter(id => this.selectedRoles[id])
        .map(id => ({id: parseInt(id)}))

      this.$emit('submit', {
        entries: [...this.entries],
        roleIds,
        personalMessage: this.personalMessage || undefined
      })
    },

    translateLimitError(errorCode, defaultMessage = null) {
      const errorMessages = {
        'role_not_allowed_for_event': this.$pgettext('collaborators.limits', 'This role is not allowed for this event'),
        'collaborator_role_limit_reached': this.$pgettext('collaborators.limits', 'Maximum limit reached for this role'),
        'collaborator_limit_reached': this.$pgettext('collaborators.limits', 'Maximum collaborator limit reached')
      }
      return errorMessages[errorCode] || defaultMessage || this.$pgettext('collaborators.notification', 'Operation failed')
    }
  }
}
</script>

<style>
.modal-i-user .modal-footer,
.modal-a-user .modal-footer,
.modal-v-user .modal-footer {
  border: 1px solid #E1DFFB !important;
}

.modal-i-user .roles-grid {
  max-height: calc(100vh - 700px);
}

.modal-a-user .roles-grid {
  max-height: calc(100vh - 670px);
}

.modal-v-user .roles-grid {
  max-height: calc(100vh - 570px);
}

.gap-2 {
  gap: 8px;
}
</style>
